import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Route, Router, UrlSegment } from '@angular/router';
import { DocumentTitleService } from './document-title.service';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { AppRoute, AppRouteData } from '../types';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private appConfig = inject(AppConfigService);
    private documentTitle = inject(DocumentTitleService);

    public url$: Observable<UrlSegment> = this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.route.firstChild),
        filter(r => !!r),
        tap((activatedRoute: ActivatedRoute) => {
            let route = activatedRoute;
            const routes = [];
            while (route.firstChild) {
                route = route.firstChild;
                routes.push(route);
            }
            let page, mainPathParam;
            if (routes.length > 1) {
                page = routes[1].snapshot.data['title'];
            }

            if (routes.length > 2) {
                mainPathParam = routes[2].snapshot.data['title'];
            }

            if (page && mainPathParam) {
                this.documentTitle.setTitle(mainPathParam);
            } else if (page) {
                this.documentTitle.setTitle(page);
            } else {
                this.documentTitle.setTitle(`Home`);
            }
        }),
        map((activatedRoute: ActivatedRoute) => activatedRoute.firstChild),
        filter(r => !!r),
        mergeMap(data => data.url),
        map(url => url[0])
    );

    public isShellSubject = new ReplaySubject<boolean>(1);
    public hasSideNavigationSubject = new ReplaySubject<boolean>(1);
    public currentAppSubject = new ReplaySubject<AppRoute>(1);
    public activatedRouteDataSubject = new ReplaySubject<AppRouteData>(1);

    public isShell = toSignal(this.isShellSubject);
    public hasSideNavigation = toSignal(this.hasSideNavigationSubject);
    public currentApp = toSignal(this.currentAppSubject);
    public activatedRouteData = toSignal(this.activatedRouteDataSubject);

    private routes: AppRoute[] = (this.router.config[1].children as AppRoute[]) || ([] as AppRoute[]);

    constructor() {
        this.url$
            .pipe(
                filter(url => !!url),
                tap(url => {
                    this.isShellSubject.next(!this.isApp(url));
                    this.hasSideNavigationSubject.next(this._hasSideNavigation(url));
                    if (this.isApp(url)) {
                        const routerConfig = this.routes.find(p => p.path === url.path);
                        this.currentAppSubject.next(routerConfig as AppRoute);
                    }
                })
            )
            .subscribe();

        this.router.events
            .pipe(
                takeUntilDestroyed(),
                filter(next => next instanceof NavigationEnd),
                map(() => this.route),
                map(route => {
                    let _route = route;
                    while (_route.firstChild) {
                        _route = _route.firstChild;
                    }
                    return _route;
                }),
                map(route => route.routeConfig?.data)
            )
            .subscribe(data => {
                this.activatedRouteDataSubject.next(data as unknown as AppRouteData);
            });
    }

    get isShell$(): Observable<boolean> {
        return this.isShellSubject.asObservable();
    }

    get apps() {
        return this.appConfig.apps;
    }

    public isApp(url: UrlSegment): boolean {
        return this.appConfig.isApp(url.path);
    }

    public _hasSideNavigation(url: UrlSegment): boolean {
        return this.appConfig.hasSideNavigation(url.path);
    }

    public getRoutes(app: Route): AppRoute[] {
        try {
            const config = (this.routes.find(route => route.path === app.path) as any)._loadedRoutes;
            return config || [];
        } catch (e) {
            return [];
        }
    }

    public getRouteDisplayName = (route: Route): string => {
        if (!route.path || route.path === '0') {
            return '';
        }
        const sentenceCase = route.path[0].toUpperCase() + route.path.slice(1);
        return sentenceCase.split('-').join(' ');
    };
}
